import type { IMeasurementViewModel, IObjectViewModel } from '@/engine/app/interfaces.ts';
import type { ViewEntity } from '@/engine/app/models/viewEntity/view.entity.ts';
import type { ICommand } from '@/engine/app/modules/core/interfaces/Command.ts';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes.ts';
import type { ViewsService } from '@/engine/app/Views.service.ts';
import type * as THREE from 'three';
import type { PointTuner } from '@/engine/PlanningAlgorithm/Tuners/PointTuner.ts';
import type { CasePhaseData } from '@/engine/app/CasePhaseData/CasePhaseData.ts';

import type { ITTVRVariableDistanceAlgoHelper } from '@/surgeries/cardiac/TTVR/interfaces.ts';
import {
  addVariableDistancesViewModels, removeVariableDistancesViewModels
} from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/VariableDistancesViewModelsHelper.ts';

export class AddVariableDistanceCommand implements ICommand {
  private readonly _targetView: ViewEntity;

  private readonly _distanceViewModel: IMeasurementViewModel;

  private readonly _pointViewModel: IObjectViewModel;

  private readonly _pointTuner: PointTuner;

  private readonly _point: THREE.Vector3;

  private readonly _viewsService: ViewsService;

  private readonly _papillaryAlgoHelper: ITTVRVariableDistanceAlgoHelper;

  private readonly _casePhaseData: CasePhaseData;

  public constructor(
    view: ViewEntity,
    variableDistanceAlgorithmHelper: ITTVRVariableDistanceAlgoHelper,
    point: THREE.Vector3,
    distanceViewModel: IMeasurementViewModel,
    pointViewModel: IObjectViewModel,
    pointTuner: PointTuner,
    viewsService: ViewsService,
    casePhaseData: CasePhaseData
  ) {
    this._targetView = view;
    this._distanceViewModel = distanceViewModel;
    this._pointViewModel = pointViewModel;
    this._point = point;
    this._papillaryAlgoHelper = variableDistanceAlgorithmHelper;
    this._pointTuner = pointTuner;
    this._casePhaseData = casePhaseData;
    this._viewsService = viewsService;
  }

  public async execute(): Promise<boolean> {
    await this._papillaryAlgoHelper.addMeasurementAsync(this._distanceViewModel.descriptor);
    await this._pointTuner.setValueAsync(this._pointViewModel.descriptor, this._point);
    this._distanceViewModel.setHighlighting(false);
    this._distanceViewModel.setSelection(false);
    this._pointViewModel.setHighlighting(false);
    this._pointViewModel.setSelection(false);
    addVariableDistancesViewModels(this._targetView, this._viewsService, this._distanceViewModel, this._pointViewModel);
    this._casePhaseData.registerInputs('Heart', [
      [this._pointViewModel.descriptor + '.dflt', new DT.Point(this._point)],
      [this._pointViewModel.descriptor + '.user', new DT.Point(this._point)],
      [this._pointViewModel.descriptor + '.flag', new DT.TuneFlag(DT.ETuneFlag.DEFAULT)] //[chebotarev] must be DEFAULT here
    ]);

    return true;
  }

  public async undo(): Promise<undefined> {
    removeVariableDistancesViewModels(
      this._targetView,
      this._viewsService,
      this._distanceViewModel,
      this._pointViewModel
    );

    this._casePhaseData.unregisterInputs('Heart', [
      this._pointViewModel.descriptor + '.dflt',
      this._pointViewModel.descriptor + '.user',
      this._pointViewModel.descriptor + '.flag'
    ]);

    await this._papillaryAlgoHelper.deleteMeasurementAsync(this._distanceViewModel.descriptor);
  }
}
